import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import ApiService from "../../../../../../services/ApiService";
import { useAuth } from "../../../../../Providers/LandmarkAuthContext";

const BASE = "landmark/oppbot";

// Bumping `version` re-fetches every active query (after approve/reject/reprocess).
export const OppBotContext = createContext({ version: 0, bump: () => {}, meta: null });

export function useOppBotContext() {
  return useContext(OppBotContext);
}

/**
 * ApiService swallows HTTP errors and resolves with either the server's error
 * body (`{ message }` from the oppbot controllers) or `{ error: "<string>" }`.
 * Email docs legitimately carry an `error` *object* and `ok:false` never
 * appears on success payloads, so only treat those exact shapes as failures.
 */
export function responseError(res) {
  if (!res || typeof res !== "object") return "Request failed";
  if (typeof res.error === "string" && res.error) return res.error;
  if (res.ok === false) return res.message || res.error || "Request failed";
  const keys = Object.keys(res);
  if (typeof res.message === "string" && keys.every((k) => ["message", "success", "status", "statusCode"].includes(k))) return res.message;
  return null;
}

export function useOppBotClient() {
  const { employee } = useAuth();
  const employeeRef = useRef(employee);
  employeeRef.current = employee;
  const employeeId = employee ? employee._id : null;
  // keyed on the employee id (not object identity) so a re-rendered auth context can't churn the client
  return useMemo(() => new ApiService({ employee: employeeRef.current }), [employeeId]); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * GET landmark/oppbot/<path>?params. Optional polling (pollMs) keeps the inbox
 * live while the pipeline is processing.
 */
export function useOppBotQuery(path, params, { enabled = true, pollMs = 0 } = {}) {
  const api = useOppBotClient();
  const { version } = useOppBotContext();
  const key = `${version}|${path}|${JSON.stringify(params || {})}`;
  const [state, setState] = useState({ data: null, loading: enabled, error: null, key });
  const latestKey = useRef(key);
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const run = useCallback(
    async (silent = false) => {
      if (!enabled) return;
      latestKey.current = key;
      if (!silent) setState((prev) => ({ ...prev, loading: prev.key !== key || !prev.data, error: null, key }));
      try {
        const res = await api.get(`${BASE}/${path}`, paramsRef.current);
        if (latestKey.current !== key) return;
        const failure = responseError(res);
        if (failure) {
          setState((prev) => ({ data: silent ? prev.data : null, loading: false, error: failure, key }));
          return;
        }
        setState({ data: res, loading: false, error: null, key });
      } catch (err) {
        if (latestKey.current !== key) return;
        setState((prev) => ({ data: silent ? prev.data : null, loading: false, error: err.message || "Request failed", key }));
      }
    },
    // `key` already encodes the serialized params, so params identity is intentionally not a dep
    [api, key, path, enabled]
  );

  useEffect(() => {
    run(false);
  }, [run]);

  useEffect(() => {
    if (!pollMs || !enabled) return undefined;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") run(true);
    }, pollMs);
    return () => clearInterval(id);
  }, [pollMs, enabled, run]);

  return { ...state, refetch: () => run(true), reload: () => run(false) };
}

export function useOppBotMutations() {
  const api = useOppBotClient();
  const { bump } = useOppBotContext();
  const wrap = useCallback(
    async (promise) => {
      const res = await promise;
      const failure = responseError(res);
      if (failure) throw new Error(failure);
      bump();
      return res;
    },
    [bump]
  );
  return useMemo(
    () => ({
      approve: (id, body) => wrap(api.post(`${BASE}/emails/${id}/approve`, body || {}, undefined, { timeout: 120000 })),
      reject: (id, note) => wrap(api.post(`${BASE}/emails/${id}/reject`, { note: note || "" })),
      reprocess: (id) => wrap(api.post(`${BASE}/emails/${id}/reprocess`, {})),
      refreshMeta: () => wrap(api.post(`${BASE}/meta/refresh`, {}, undefined, { timeout: 60000 })),
      testIngest: (file, { forceReprocess = false } = {}) => {
        const form = new FormData();
        form.append("eml", file, file.name || "email.eml");
        return wrap(
          api.post(`${BASE}/test-ingest${forceReprocess ? "?forceReprocess=true" : ""}`, form, undefined, {
            timeout: 60000,
            headers: { "Content-Type": "multipart/form-data" },
          })
        );
      },
      rawUrl: (id) => `${api.apiUrl}${BASE}/emails/${id}/raw`,
    }),
    [api, wrap]
  );
}
